import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  History,
  Payment,
} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import DynamicTable from "../../../components/lab-dynamic-table";
import DateRangeSelector from "../../../components/DateRangeSelector";
import { formatCurrency } from "../../../utils/numberUtils";
import { formatDateOnly, formatDateTime } from "../../../utils/dateTimeUtils";

interface ExpenseCategory {
  _id: string;
  categoryName: string;
}

interface UserOption {
  _id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

interface ExpensePayment {
  _id: string;
  voucher?: number;
  expenseCategoryId: string;
  expenseCategory?: string;
  category?: { _id: string; categoryName: string };
  description?: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  paymentStatus: string;
  paymentMethod: string;
  paymentDate?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: { _id: string; fullName?: string; firstName?: string };
  updatedBy?: { _id: string; fullName?: string; firstName?: string };
  createdByName?: string;
  updatedByName?: string;
}

interface PaymentTransaction {
  _id: string;
  voucher?: number;
  expensePaymentId?: string;
  expenseCategory?: string;
  category?: { categoryName?: string };
  paymentAmount: number;
  paymentMethod: string;
  paymentDate?: string;
  description?: string;
  recordedByName?: string;
  recordedByUser?: { fullName?: string; firstName?: string };
  previousPaidAmount: number;
  newPaidAmount: number;
  previousRemainingAmount: number;
  newRemainingAmount: number;
  previousPaymentStatus: string;
  newPaymentStatus: string;
  attachmentUrl?: string | null;
  createdAt?: string;
  expensePayment?: {
    voucher?: number;
    totalAmount?: number;
    remainingAmount?: number;
    paymentStatus?: string;
    description?: string;
  };
}

const PAYMENT_METHODS = ["Cash", "EasyPaisa", "JazzCash", "Bank"];
const PAYMENT_STATUSES = ["Unpaid", "Partially Paid", "Fully Paid"];

const emptyForm = {
  expenseCategoryId: "",
  totalAmount: "",
  paidAmount: "",
  paymentMethod: "",
  description: "",
  paymentDate: new Date().toISOString().slice(0, 10),
};

const resolveStatus = (totalAmount: number, paidAmount: number) => {
  const total = Number(totalAmount) || 0;
  const paid = Number(paidAmount) || 0;
  if (paid <= 0) return "Unpaid";
  if (total > 0 && paid >= total) return "Fully Paid";
  return "Partially Paid";
};

const statusColor = (status: string) => {
  if (status === "Fully Paid") return "success";
  if (status === "Partially Paid") return "warning";
  return "default";
};

const ExpensePaymentsPage: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];
  const token = localStorage.getItem("token");
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const expensePermissions = profile?.data?.accessConfigurations?.Expense || {};
  const authHeaders = { Authorization: `Bearer ${token}` };
  const apiBase = `${process.env.REACT_APP_ADMIN_API_URL}/expense-payments`;

  const [activeTab, setActiveTab] = useState(0);
  const [rows, setRows] = useState<ExpensePayment[]>([]);
  const [historyAllRows, setHistoryAllRows] = useState<PaymentTransaction[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(20);

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [dateRange, setDateRange] = useState("Today");
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("");
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<UserOption | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [search, setSearch] = useState("");

  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [openMarkPaidDialog, setOpenMarkPaidDialog] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExpensePayment | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [formFile, setFormFile] = useState<File | null>(null);
  const [formHistory, setFormHistory] = useState<PaymentTransaction[]>([]);
  const [saving, setSaving] = useState(false);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState<ExpensePayment | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentTransaction[]>([]);
  const [paymentForm, setPaymentForm] = useState({
    paymentAmount: "",
    paymentMethod: "Cash",
    description: "",
    paymentDate: today,
  });
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [paying, setPaying] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState<PaymentTransaction[]>([]);
  const [historyTarget, setHistoryTarget] = useState<ExpensePayment | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExpensePayment | null>(null);

  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  const handleOpenImageModal = (attachment: File | string | null) => {
    if (attachment instanceof File) {
      setSelectedImageUrl(URL.createObjectURL(attachment));
    } else if (typeof attachment === "string" && attachment) {
      setSelectedImageUrl(attachment);
    } else {
      return;
    }
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setSelectedImageUrl(null);
  };

  const handlePickImage = (
    file: File | undefined,
    setFile: (file: File | null) => void
  ) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Select valid image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max size 5MB");
      return;
    }
    setFile(file);
  };

  const renderAttachmentPicker = (
    file: File | null,
    setFile: (file: File | null) => void,
    helperText?: string
  ) => (
    <Box>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        Bill attachment
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Button
          component="label"
          size="small"
          sx={{
            background: "#f1f1f3ff",
            textTransform: "none",
            borderRadius: 2,
            "&:hover": { background: "#e0e0e0" },
          }}
        >
          Upload Image
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handlePickImage(e.target.files?.[0], setFile)}
          />
        </Button>
        {file && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <img
              src={URL.createObjectURL(file)}
              alt="Attachment"
              style={{
                minWidth: 80,
                maxHeight: 100,
                objectFit: "contain",
                borderRadius: 4,
                cursor: "pointer",
              }}
              onClick={() => handleOpenImageModal(file)}
            />
            <IconButton size="small" onClick={() => setFile(null)}>
              <Delete sx={{ fontSize: 16, color: "#FF1F1F" }} />
            </IconButton>
          </Box>
        )}
      </Box>
      {helperText && (
        <Typography sx={{ fontSize: 11, color: "#666", mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );

  const renderAttachmentThumb = (url?: string | null) =>
    url ? (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <img
          src={url}
          alt="Attachment"
          style={{
            width: 40,
            height: 35,
            objectFit: "cover",
            borderRadius: 4,
            cursor: "pointer",
          }}
          onClick={() => handleOpenImageModal(url)}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const nextSibling = e.currentTarget.nextSibling as HTMLElement;
            if (nextSibling) nextSibling.style.display = "block";
          }}
        />
        <Typography variant="body2" sx={{ display: "none", fontSize: 12 }}>
          Unable to load image
        </Typography>
      </Box>
    ) : (
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        No image
      </Typography>
    );

  const autoStatus = useMemo(() => {
    if (editing) {
      return resolveStatus(Number(form.totalAmount), Number(editing.paidAmount || 0));
    }
    return resolveStatus(Number(form.totalAmount), Number(form.paidAmount || 0));
  }, [form.totalAmount, form.paidAmount, editing]);

  const autoRemaining = useMemo(() => {
    const total = Number(form.totalAmount) || 0;
    const paid = editing
      ? Number(editing.paidAmount || 0)
      : Number(form.paidAmount || 0);
    return Math.max(0, Math.round((total - paid) * 100) / 100);
  }, [form.totalAmount, form.paidAmount, editing]);

  const isPaymentMethodRequired = useMemo(() => {
    const paid = editing
      ? Number(editing.paidAmount || 0)
      : Number(form.paidAmount || 0);
    return paid > 0;
  }, [form.paidAmount, editing]);

  const paymentPreviewStatus = useMemo(() => {
    if (!paymentTarget) return "Unpaid";
    const total = Number(paymentTarget.totalAmount) || 0;
    const currentPaid = Number(paymentTarget.paidAmount) || 0;
    const nextPaid = currentPaid + (Number(paymentForm.paymentAmount) || 0);
    return resolveStatus(total, nextPaid);
  }, [paymentTarget, paymentForm.paymentAmount]);

  const paymentPreviewRemaining = useMemo(() => {
    if (!paymentTarget) return 0;
    const remaining = Number(paymentTarget.remainingAmount) || 0;
    const amount = Number(paymentForm.paymentAmount) || 0;
    return Math.max(0, Math.round((remaining - amount) * 100) / 100);
  }, [paymentTarget, paymentForm.paymentAmount]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.total += Number(row.totalAmount || 0);
        acc.paid += Number(row.paidAmount || 0);
        acc.remaining += Number(row.remainingAmount || 0);
        return acc;
      },
      { total: 0, paid: 0, remaining: 0 }
    );
  }, [rows]);

  const isPayableExpense = (e: ExpensePayment) =>
    e.paymentStatus === "Unpaid" ||
    e.paymentStatus === "Partially Paid" ||
    Number(e.remainingAmount || 0) > 0;

  const payableExpenses = useMemo(
    () => rows.filter(isPayableExpense),
    [rows]
  );

  const allUnpaidSelected =
    payableExpenses.length > 0 &&
    payableExpenses.every((e) => selectedExpenseIds.includes(e._id));

  const someUnpaidSelected =
    payableExpenses.some((e) => selectedExpenseIds.includes(e._id)) &&
    !allUnpaidSelected;

  const selectedExpensesForPay = useMemo(
    () =>
      rows.filter(
        (e) => selectedExpenseIds.includes(e._id) && isPayableExpense(e)
      ),
    [rows, selectedExpenseIds]
  );

  const selectedPayTotal = useMemo(
    () =>
      selectedExpensesForPay.reduce(
        (sum, e) => sum + Number(e.remainingAmount || e.totalAmount || 0),
        0
      ),
    [selectedExpensesForPay]
  );

  const displayName = (
    user?: { fullName?: string; firstName?: string },
    fallback = "--"
  ) => user?.fullName || user?.firstName || fallback;

  const filterControlSx = {
    background: "#F5F5FB",
    borderRadius: "12px",
    "& fieldset": { border: "none" },
  };

  const filterLabelSx = {
    fontSize: "12px",
    fontWeight: "500",
    color: "#000",
    mb: 0.5,
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/expense-category/getAllBySearch`,
        { headers: authHeaders }
      );
      if (response.data?.isSuccess) setCategories(response.data.data || []);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch expense categories"
      );
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/findAllUsers`,
        { headers: authHeaders }
      );
      if (response.data?.isSuccess) setUsers(response.data.data || []);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch users"
      );
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await axios.get(apiBase, {
        headers: authHeaders,
        params: {
          fromDate,
          toDate,
          ...(selectedCategory && { expenseCategoryId: selectedCategory._id }),
          ...(selectedStatus && { paymentStatus: selectedStatus }),
          ...(selectedCreatedBy && { userId: selectedCreatedBy._id }),
          ...(search.trim() && { search: search.trim() }),
        },
      });
      if (response.data?.isSuccess) {
        const data = response.data.data || [];
        setRows(data);
        setSelectedExpenseIds((prev) =>
          prev.filter((id) =>
            data.some(
              (row: ExpensePayment) => row._id === id && isPayableExpense(row)
            )
          )
        );
      } else {
        toast.error(response.data?.message || "Failed to fetch expense payments");
        setRows([]);
        setSelectedExpenseIds([]);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch expense payments"
      );
      setRows([]);
      setSelectedExpenseIds([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllHistories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/transactions/all`, {
        headers: authHeaders,
        params: {
          fromDate,
          toDate,
          ...(selectedCategory && { expenseCategoryId: selectedCategory._id }),
          ...(selectedCreatedBy && { userId: selectedCreatedBy._id }),
          ...(search.trim() && { search: search.trim() }),
        },
      });
      if (response.data?.isSuccess) setHistoryAllRows(response.data.data || []);
      else {
        toast.error(response.data?.message || "Failed to fetch payment histories");
        setHistoryAllRows([]);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch payment histories"
      );
      setHistoryAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoryForExpense = async (expenseId: string) => {
    const response = await axios.get(`${apiBase}/${expenseId}/transactions`, {
      headers: authHeaders,
    });
    if (response.data?.isSuccess) {
      return (response.data.data?.transactions || []) as PaymentTransaction[];
    }
    return [];
  };

  useEffect(() => {
    fetchCategories();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 0) fetchRows();
    else {
      setSelectedExpenseIds([]);
      fetchAllHistories();
    }
  }, [
    activeTab,
    fromDate,
    toDate,
    selectedCategory,
    selectedStatus,
    selectedCreatedBy,
  ]);

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    setPage(0);
    setHistoryPage(0);
  };

  const handleToggleSelectAllUnpaid = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) setSelectedExpenseIds(payableExpenses.map((e) => e._id));
    else setSelectedExpenseIds([]);
  };

  const handleToggleExpenseSelect = (expense: ExpensePayment) => {
    if (!isPayableExpense(expense)) return;
    setSelectedExpenseIds((prev) =>
      prev.includes(expense._id)
        ? prev.filter((id) => id !== expense._id)
        : [...prev, expense._id]
    );
  };

  const handleOpenMarkPaidDialog = () => {
    if (!selectedExpenseIds.length) {
      toast.warn("Please select at least one unpaid or partially paid expense");
      return;
    }
    setOpenMarkPaidDialog(true);
  };

  const handleBulkMarkAsPaid = async () => {
    if (!selectedExpenseIds.length) {
      toast.warn("Please select at least one unpaid or partially paid expense");
      return;
    }
    setMarkingPaid(true);
    try {
      const response = await axios.post(
        `${apiBase}/bulkMarkAsPaid`,
        { ids: selectedExpenseIds },
        { headers: authHeaders }
      );
      if (response.data?.isSuccess) {
        toast.success(
          response.data.message || "Selected expenses marked as Paid"
        );
        setSelectedExpenseIds([]);
        setOpenMarkPaidDialog(false);
        fetchRows();
        if (activeTab === 1) fetchAllHistories();
      } else {
        toast.error(
          response.data?.message || "Failed to mark expenses as Paid"
        );
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while marking expenses as Paid"
      );
    } finally {
      setMarkingPaid(false);
    }
  };

  const exportBtnSx = {
    minWidth: { xs: "auto", md: "90px" },
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    mb: 0.5,
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    },
  } as const;

  const handleDownloadExcel = () => {
    if (!expensePermissions.print_report) {
      toast.error("You do not have permission to download reports.");
      return;
    }

    if (activeTab === 0) {
      if (!rows.length) {
        toast.warn("No data to export.");
        return;
      }
      const headers = [
        "Sr#",
        "Voucher#",
        "Category",
        "Description",
        "Total Amount",
        "Paid Amount",
        "Remaining",
        "Status",
        "Payment Date",
        "Created At",
        "Created By",
        "Updated By",
      ];
      const excelData = rows.map((item, index) => ({
        "Sr#": index + 1,
        "Voucher#": item.voucher ?? "--",
        Category: item.category?.categoryName || item.expenseCategory || "--",
        Description: item.description || "--",
        "Total Amount": Number(item.totalAmount || 0).toFixed(2),
        "Paid Amount": Number(item.paidAmount || 0).toFixed(2),
        Remaining: Number(item.remainingAmount || 0).toFixed(2),
        Status: item.paymentStatus || "--",
        "Payment Date": item.paymentDate
          ? formatDateOnly(item.paymentDate)
          : "--",
        "Created At": item.createdAt ? formatDateTime(item.createdAt) : "--",
        "Created By": displayName(item.createdBy, item.createdByName || "--"),
        "Updated By": displayName(item.updatedBy, item.updatedByName || "--"),
      }));
      const ws = XLSX.utils.json_to_sheet(excelData, { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Expense Payments");
      XLSX.writeFile(
        wb,
        `Expense_Payments_Report-${new Date().toLocaleDateString()}-${new Date().toLocaleTimeString()}.xlsx`
      );
      toast.success("Excel file downloaded successfully");
      return;
    }

    const historyExportRows = historyAllRows.filter((tx) => {
      if (selectedStatus && tx.newPaymentStatus !== selectedStatus) return false;
      if (selectedMethod && tx.paymentMethod !== selectedMethod) return false;
      return true;
    });
    if (!historyExportRows.length) {
      toast.warn("No data to export.");
      return;
    }
    const headers = [
      "Sr#",
      "Voucher#",
      "Category",
      "Payment Amount",
      "Prev Remaining",
      "Remaining",
      "Status",
      "Method",
      "Payment Date",
      "Recorded By",
      "Description",
    ];
    const excelData = historyExportRows.map((tx, index) => ({
      "Sr#": index + 1,
      "Voucher#": tx.voucher ?? tx.expensePayment?.voucher ?? "--",
      Category: tx.category?.categoryName || tx.expenseCategory || "--",
      "Payment Amount": Number(tx.paymentAmount || 0).toFixed(2),
      "Prev Remaining": Number(tx.previousRemainingAmount || 0).toFixed(2),
      Remaining: Number(tx.newRemainingAmount || 0).toFixed(2),
      Status: tx.newPaymentStatus || "--",
      Method: tx.paymentMethod || "--",
      "Payment Date": tx.paymentDate
        ? formatDateTime(tx.paymentDate)
        : tx.createdAt
          ? formatDateTime(tx.createdAt)
          : "--",
      "Recorded By": displayName(tx.recordedByUser, tx.recordedByName || "--"),
      Description: tx.description || "--",
    }));
    const ws = XLSX.utils.json_to_sheet(excelData, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payment Histories");
    XLSX.writeFile(
      wb,
      `Payment_Histories_Report-${new Date().toLocaleDateString()}-${new Date().toLocaleTimeString()}.xlsx`
    );
    toast.success("Excel file downloaded successfully");
  };

  const handlePrint = () => {
    if (!expensePermissions.print_report) {
      toast.error("You do not have permission to print reports.");
      return;
    }

    const isHistory = activeTab === 1;
    const historyExportRows = historyAllRows.filter((tx) => {
      if (selectedStatus && tx.newPaymentStatus !== selectedStatus) return false;
      if (selectedMethod && tx.paymentMethod !== selectedMethod) return false;
      return true;
    });
    const sourceRows = isHistory ? historyExportRows : rows;
    if (!sourceRows.length) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    let yPosition = margin;

    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text(
        isHistory ? "PAYMENT HISTORIES REPORT" : "EXPENSE PAYMENTS REPORT",
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    let head: string[][] = [];
    let body: (string | number)[][] = [];

    if (isHistory) {
      head = [
        [
          "SR#",
          "VOUCHER#",
          "CATEGORY",
          "PAYMENT AMOUNT",
          "PREV REMAINING",
          "REMAINING",
          "STATUS",
          "METHOD",
          "PAYMENT DATE",
          "RECORDED BY",
          "DESCRIPTION",
        ],
      ];
      body = historyExportRows.map((tx, index) => [
        index + 1,
        tx.voucher ?? tx.expensePayment?.voucher ?? "--",
        tx.category?.categoryName || tx.expenseCategory || "--",
        Number(tx.paymentAmount || 0).toFixed(2),
        Number(tx.previousRemainingAmount || 0).toFixed(2),
        Number(tx.newRemainingAmount || 0).toFixed(2),
        tx.newPaymentStatus || "--",
        tx.paymentMethod || "--",
        tx.paymentDate
          ? formatDateTime(tx.paymentDate)
          : tx.createdAt
            ? formatDateTime(tx.createdAt)
            : "--",
        displayName(tx.recordedByUser, tx.recordedByName || "--"),
        tx.description || "--",
      ]);
      const totalPayment = historyExportRows.reduce(
        (sum, tx) => sum + Number(tx.paymentAmount || 0),
        0
      );
      body.push([
        "",
        "",
        "TOTAL",
        Number(totalPayment).toFixed(2),
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
    } else {
      head = [
        [
          "SR#",
          "VOUCHER#",
          "CATEGORY",
          "DESCRIPTION",
          "TOTAL AMOUNT",
          "PAID AMOUNT",
          "REMAINING",
          "STATUS",
          "PAYMENT DATE",
          "CREATED AT",
          "CREATED BY",
          "UPDATED BY",
        ],
      ];
      body = rows.map((item, index) => [
        index + 1,
        item.voucher ?? "--",
        item.category?.categoryName || item.expenseCategory || "--",
        item.description || "--",
        Number(item.totalAmount || 0).toFixed(2),
        Number(item.paidAmount || 0).toFixed(2),
        Number(item.remainingAmount || 0).toFixed(2),
        item.paymentStatus || "--",
        item.paymentDate ? formatDateOnly(item.paymentDate) : "--",
        item.createdAt ? formatDateTime(item.createdAt) : "--",
        displayName(item.createdBy, item.createdByName || "--"),
        displayName(item.updatedBy, item.updatedByName || "--"),
      ]);
      body.push([
        "",
        "",
        "",
        "TOTAL",
        Number(totals.total).toFixed(2),
        Number(totals.paid).toFixed(2),
        Number(totals.remaining).toFixed(2),
        "",
        "",
        "",
        "",
        "",
      ]);
    }

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 6,
        halign: "center",
        valign: "middle",
        cellPadding: 2,
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8,
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: (data: any) => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 5.8;
        }
      },
      didDrawPage: (data: any) => {
        const pageSize = doc.internal.pageSize;
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      },
    });

    doc.save(
      isHistory
        ? `Payment_Histories_Report-${fromDate}_to_${toDate}.pdf`
        : `Expense_Payments_Report-${fromDate}_to_${toDate}.pdf`
    );
    toast.success("PDF downloaded successfully");
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setFormFile(null);
    setFormHistory([]);
    setFormOpen(true);
  };

  const openEdit = async (item: ExpensePayment) => {
    setEditing(item);
    setFormFile(null);
    setForm({
      expenseCategoryId: item.expenseCategoryId || item.category?._id || "",
      totalAmount: String(item.totalAmount ?? ""),
      paidAmount: String(item.paidAmount ?? ""),
      paymentMethod: item.paymentMethod || "Cash",
      description: item.description || "",
      paymentDate: item.paymentDate
        ? new Date(item.paymentDate).toISOString().slice(0, 10)
        : today,
    });
    setFormOpen(true);
    try {
      setFormHistory(await loadHistoryForExpense(item._id));
    } catch {
      setFormHistory([]);
    }
  };

  const submitForm = async () => {
    if (!form.expenseCategoryId) {
      toast.error("Please select an expense category");
      return;
    }
    const totalAmount = Number(form.totalAmount);
    if (!(totalAmount > 0)) {
      toast.error("Total amount must be greater than zero");
      return;
    }

    const paidForMethodCheck = editing
      ? Number(editing.paidAmount || 0)
      : Number(form.paidAmount || 0);
    if (paidForMethodCheck > 0 && !form.paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    const selectedCat = categories.find((c) => c._id === form.expenseCategoryId);
    const payload: any = {
      expenseCategoryId: form.expenseCategoryId,
      expenseCategory: selectedCat?.categoryName || "",
      totalAmount,
      description: form.description,
      paymentDate: form.paymentDate,
    };
    if (form.paymentMethod) {
      payload.paymentMethod = form.paymentMethod;
    }

    if (!editing) {
      const paidAmount = Number(form.paidAmount || 0);
      if (paidAmount < 0) {
        toast.error("Paid amount cannot be negative");
        return;
      }
      if (paidAmount > totalAmount) {
        toast.error("Paid amount cannot exceed total amount");
        return;
      }
      payload.paidAmount = paidAmount;
    }

    setSaving(true);
    try {
      let response;
      if (editing) {
        response = await axios.patch(`${apiBase}/${editing._id}`, payload, {
          headers: authHeaders,
        });
      } else if (formFile && Number(form.paidAmount || 0) > 0) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            formData.append(key, String(value));
          }
        });
        formData.append("file", formFile);
        response = await axios.post(apiBase, formData, {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        response = await axios.post(apiBase, payload, { headers: authHeaders });
      }

      if (response.data?.isSuccess) {
        toast.success(response.data.message || "Saved successfully");
        setFormOpen(false);
        setFormFile(null);
        fetchRows();
        if (activeTab === 1) fetchAllHistories();
      } else {
        toast.error(response.data?.message || "Failed to save");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const openAddPayment = async (item: ExpensePayment) => {
    if (item.paymentStatus === "Fully Paid" || Number(item.remainingAmount) <= 0) {
      toast.warning("This expense is already fully paid");
      return;
    }
    setPaymentTarget(item);
    setPaymentForm({
      paymentAmount: "",
      paymentMethod: item.paymentMethod || "Cash",
      description: "",
      paymentDate: today,
    });
    setPaymentFile(null);
    setPaymentOpen(true);
    try {
      setPaymentHistory(await loadHistoryForExpense(item._id));
    } catch {
      setPaymentHistory([]);
    }
  };

  const submitPayment = async () => {
    if (!paymentTarget) return;
    const paymentAmount = Number(paymentForm.paymentAmount);
    if (!(paymentAmount > 0)) {
      toast.error("Payment amount must be greater than zero");
      return;
    }
    if (paymentAmount > Number(paymentTarget.remainingAmount || 0)) {
      toast.error(
        `Payment cannot exceed remaining amount (${paymentTarget.remainingAmount})`
      );
      return;
    }

    const formData = new FormData();
    formData.append("paymentAmount", String(paymentAmount));
    formData.append("paymentMethod", paymentForm.paymentMethod);
    formData.append("description", paymentForm.description || "");
    formData.append("paymentDate", paymentForm.paymentDate);
    if (paymentFile) formData.append("file", paymentFile);

    setPaying(true);
    try {
      const response = await axios.post(
        `${apiBase}/${paymentTarget._id}/payments`,
        formData,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data?.isSuccess) {
        toast.success(response.data.message || "Payment recorded");
        setPaymentOpen(false);
        fetchRows();
        if (activeTab === 1) fetchAllHistories();
      } else {
        toast.error(response.data?.message || "Failed to record payment");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to record payment");
    } finally {
      setPaying(false);
    }
  };

  const openHistory = async (item: ExpensePayment) => {
    setHistoryTarget(item);
    setHistoryOpen(true);
    try {
      setHistoryRows(await loadHistoryForExpense(item._id));
    } catch (error: any) {
      setHistoryRows([]);
      toast.error(
        error?.response?.data?.message || "Failed to load payment history"
      );
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await axios.delete(`${apiBase}/${deleteTarget._id}`, {
        headers: authHeaders,
      });
      if (response.data?.isSuccess) {
        toast.success(response.data.message || "Deleted successfully");
        setDeleteOpen(false);
        setDeleteTarget(null);
        fetchRows();
        if (activeTab === 1) fetchAllHistories();
      } else {
        toast.error(response.data?.message || "Failed to delete");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete");
    }
  };

  const renderHistoryList = (list: PaymentTransaction[]) => {
    if (!list.length) {
      return (
        <Typography sx={{ py: 1.5, color: "#666", fontSize: 13 }}>
          No payment history yet.
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {list.map((tx, index) => (
          <Box
            key={tx._id}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 1.5,
              p: 1.25,
              background: "#fafafa",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              mb={0.75}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Payment #{list.length - index}
              </Typography>
              <Chip
                size="small"
                label={tx.newPaymentStatus || "--"}
                color={statusColor(tx.newPaymentStatus) as any}
              />
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Amount</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {formatCurrency(tx.paymentAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>
                  Remaining After
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "red" }}>
                  {formatCurrency(tx.newRemainingAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Method</Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {tx.paymentMethod || "--"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Date</Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {tx.paymentDate
                    ? formatDateTime(tx.paymentDate)
                    : tx.createdAt
                      ? formatDateTime(tx.createdAt)
                      : "--"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Prev Paid</Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {formatCurrency(tx.previousPaidAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>New Paid</Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {formatCurrency(tx.newPaidAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>
                  Prev Remaining
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {formatCurrency(tx.previousRemainingAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Recorded By</Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {displayName(tx.recordedByUser, tx.recordedByName || "--")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Description</Typography>
                <Typography sx={{ fontSize: 13 }}>{tx.description || "--"}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ fontSize: 11, color: "#666" }}>Attachment</Typography>
                {renderAttachmentThumb(tx.attachmentUrl)}
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>
    );
  };

  const filteredHistories = useMemo(() => {
    return historyAllRows.filter((tx) => {
      if (selectedStatus && tx.newPaymentStatus !== selectedStatus) return false;
      if (selectedMethod && tx.paymentMethod !== selectedMethod) return false;
      return true;
    });
  }, [historyAllRows, selectedStatus, selectedMethod]);

  const historyPaymentTotal = useMemo(
    () =>
      filteredHistories.reduce(
        (sum, tx) => sum + Number(tx.paymentAmount || 0),
        0
      ),
    [filteredHistories]
  );

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const pagedHistories = filteredHistories.slice(
    historyPage * historyRowsPerPage,
    historyPage * historyRowsPerPage + historyRowsPerPage
  );

  const tableData = pagedRows.map((item, index) => [
    <Checkbox
      key={`sel-${item._id}`}
      size="small"
      checked={selectedExpenseIds.includes(item._id)}
      disabled={!isPayableExpense(item)}
      onChange={() => handleToggleExpenseSelect(item)}
      inputProps={{ "aria-label": `Select expense ${item.voucher}` }}
    />,
    page * rowsPerPage + index + 1,
    item.voucher ?? "--",
    item.category?.categoryName || item.expenseCategory || "--",
    item.description || "--",
    formatCurrency(item.totalAmount || 0),
    formatCurrency(item.paidAmount || 0),
    <span
      key={`rem-${item._id}`}
      style={{
        color: Number(item.remainingAmount) > 0 ? "red" : "inherit",
        fontWeight: Number(item.remainingAmount) > 0 ? 600 : 400,
      }}
    >
      {formatCurrency(item.remainingAmount || 0)}
    </span>,
    <Chip
      key={`st-${item._id}`}
      size="small"
      label={item.paymentStatus || "--"}
      color={statusColor(item.paymentStatus) as any}
    />,
    item.paymentDate ? formatDateOnly(item.paymentDate) : "--",
    item.createdAt ? formatDateTime(item.createdAt) : "--",
    displayName(item.createdBy, item.createdByName || "--"),
    displayName(item.updatedBy, item.updatedByName || "--"),
  ]);

  const historyTableData = pagedHistories.map((tx, index) => [
    historyPage * historyRowsPerPage + index + 1,
    tx.voucher ?? tx.expensePayment?.voucher ?? "--",
    tx.category?.categoryName || tx.expenseCategory || "--",
    formatCurrency(tx.paymentAmount || 0),
    formatCurrency(tx.previousRemainingAmount || 0),
    <span key={`hrem-${tx._id}`} style={{ color: "red", fontWeight: 600 }}>
      {formatCurrency(tx.newRemainingAmount || 0)}
    </span>,
    <Chip
      key={`hst-${tx._id}`}
      size="small"
      label={tx.newPaymentStatus || "--"}
      color={statusColor(tx.newPaymentStatus) as any}
    />,
    tx.paymentMethod || "--",
    tx.paymentDate
      ? formatDateTime(tx.paymentDate)
      : tx.createdAt
        ? formatDateTime(tx.createdAt)
        : "--",
    displayName(tx.recordedByUser, tx.recordedByName || "--"),
    tx.description || "--",
    <Box key={`att-${tx._id}`}>{renderAttachmentThumb(tx.attachmentUrl)}</Box>,
  ]);

  return (
    <Box sx={{ p: 1, background: "#f5f7fb", minHeight: "100%" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1}
        sx={{ background: "#fff", borderRadius: "12px", mb: 1 }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
          Expense Payments
        </Typography>
        {activeTab === 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={openCreate}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            }}
          >
            Add Expense Payment
          </Button>
        )}
      </Box>

      <Box sx={{ background: "#fff", borderRadius: "12px", px: 1, mb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, value) => {
            setActiveTab(value);
            setPage(0);
            setHistoryPage(0);
          }}
        >
          <Tab label="Expense Payments" sx={{ textTransform: "none" }} />
          <Tab label="Payment Histories" sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 1.5,
          mb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {activeTab === 0 && selectedExpenseIds.length > 0 && (
            <Button
              variant="contained"
              size="small"
              disabled={markingPaid}
              onClick={handleOpenMarkPaidDialog}
              sx={{
                minWidth: { xs: "auto", md: "140px" },
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #2e7d32 30%, #66bb6a 90%)",
                color: "#ffffff",
                mb: 0.5,
                "&:hover": {
                  background: "linear-gradient(45deg, #1b5e20 30%, #43a047 90%)",
                },
              }}
            >
              {markingPaid
                ? "Updating..."
                : `Mark Paid (${selectedExpenseIds.length})`}
            </Button>
          )}

          {expensePermissions.print_report && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handlePrint}
                sx={exportBtnSx}
              >
                PDF
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
                sx={exportBtnSx}
              >
                Excel
              </Button>
            </Box>
          )}

          {activeTab === 0 && (
            <Box sx={{ width: { xs: "100%", md: "125px" } }}>
              <Typography sx={filterLabelSx}>Payment Status</Typography>
              <FormControl fullWidth size="small" sx={{ maxWidth: "125px" }}>
                <Select
                  value={selectedStatus}
                  className="custom-input"
                  displayEmpty
                  onChange={(e) => {
                    setPage(0);
                    setSelectedStatus(e.target.value);
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: "12px" }}>
                    All
                  </MenuItem>
                  {PAYMENT_STATUSES.map((status) => (
                    <MenuItem key={status} value={status} sx={{ fontSize: "12px" }}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {activeTab === 1 && (
            <>
              <Box sx={{ width: { xs: "100%", md: "125px" } }}>
                <Typography sx={filterLabelSx}>Payment Status</Typography>
                <FormControl fullWidth size="small" sx={{ maxWidth: "125px" }}>
                  <Select
                    value={selectedStatus}
                    className="custom-input"
                    displayEmpty
                    onChange={(e) => {
                      setHistoryPage(0);
                      setSelectedStatus(e.target.value);
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: "12px" }}>
                      All
                    </MenuItem>
                    {PAYMENT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status} sx={{ fontSize: "12px" }}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ width: { xs: "100%", md: "125px" } }}>
                <Typography sx={filterLabelSx}>Payment Method</Typography>
                <FormControl fullWidth size="small" sx={{ maxWidth: "125px" }}>
                  <Select
                    value={selectedMethod}
                    className="custom-input"
                    displayEmpty
                    onChange={(e) => {
                      setHistoryPage(0);
                      setSelectedMethod(e.target.value);
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: "12px" }}>
                      All
                    </MenuItem>
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method} sx={{ fontSize: "12px" }}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          <Box sx={{ width: { xs: "100%", md: "150px" } }}>
            <Typography sx={filterLabelSx}>Created By</Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={users}
              sx={filterControlSx}
              getOptionLabel={(option) =>
                option.fullName ||
                `${option.firstName || ""} ${option.lastName || ""}`.trim() ||
                ""
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={selectedCreatedBy}
              onChange={(_e, newValue) => {
                setPage(0);
                setHistoryPage(0);
                setSelectedCreatedBy(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select User" />
              )}
              clearOnEscape
            />
          </Box>

          <Box sx={{ width: { xs: "100%", md: "150px" } }}>
            <Typography sx={filterLabelSx}>Search by Category</Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={categories}
              sx={filterControlSx}
              getOptionLabel={(option) => option.categoryName || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={selectedCategory}
              onChange={(_e, newValue) => {
                setPage(0);
                setHistoryPage(0);
                setSelectedCategory(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Category" />
              )}
              clearOnEscape
            />
          </Box>

          <Box sx={{ width: { xs: "100%", md: "150px" } }}>
            <Typography sx={filterLabelSx}>Search</Typography>
            <TextField
              size="small"
              className="custom-input"
              placeholder="Voucher ID or Category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(0);
                  setHistoryPage(0);
                  if (activeTab === 0) fetchRows();
                  else fetchAllHistories();
                }
              }}
              sx={filterControlSx}
              fullWidth
            />
          </Box>

          <DateRangeSelector
            compact
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            permissions={expensePermissions}
          />
        </Box>
      </Box>

      <Box sx={{ background: "#fff", borderRadius: "12px", p: 1 }}>
        {activeTab === 0 ? (
          <DynamicTable
            headers={[
              <Checkbox
                key="select-all"
                size="small"
                sx={{
                  color: "#fff",
                  p: 0,
                  "&.Mui-checked": { color: "#fff" },
                  "&.MuiCheckbox-indeterminate": { color: "#fff" },
                }}
                checked={allUnpaidSelected}
                indeterminate={someUnpaidSelected}
                disabled={payableExpenses.length === 0}
                onChange={handleToggleSelectAllUnpaid}
                inputProps={{
                  "aria-label": "Select all unpaid and partially paid expenses",
                }}
              />,
              "SN",
              "VOUCHER",
              "CATEGORY",
              "DESCRIPTION",
              "TOTAL AMOUNT",
              "PAID AMOUNT",
              "REMAINING",
              "STATUS",
              "PAYMENT DATE",
              "CREATED AT",
              "CREATED BY",
              "UPDATED BY",
              "ACTIONS",
            ]}
            data={tableData}
            loading={loading}
            height="420px"
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={rows.length}
            totalAmount={totals.total}
            totalPaidAmount={totals.paid}
            totalUnpaidAmount={totals.remaining}
            handleChangePage={(_e, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            actionButtons={[
              {
                tooltip: "Add Payment",
                icon: <Payment sx={{ color: "#2e7d32", fontSize: 18 }} />,
                onClick: (rowIndex: number) => {
                  const item = pagedRows[rowIndex];
                  if (item) openAddPayment(item);
                },
                disabled: (rowIndex: number) =>
                  pagedRows[rowIndex]?.paymentStatus === "Fully Paid" ||
                  Number(pagedRows[rowIndex]?.remainingAmount || 0) <= 0,
              },
              {
                tooltip: "Payment History",
                icon: <History sx={{ color: "#1565c0", fontSize: 18 }} />,
                onClick: (rowIndex: number) => {
                  const item = pagedRows[rowIndex];
                  if (item) openHistory(item);
                },
              },
              {
                tooltip: "Edit",
                icon: <Edit sx={{ color: "#ed6c02", fontSize: 18 }} />,
                onClick: (rowIndex: number) => {
                  const item = pagedRows[rowIndex];
                  if (item) openEdit(item);
                },
              },
              {
                tooltip: "Delete",
                icon: <Delete sx={{ color: "red", fontSize: 18 }} />,
                onClick: (rowIndex: number) => {
                  const item = pagedRows[rowIndex];
                  if (item) {
                    setDeleteTarget(item);
                    setDeleteOpen(true);
                  }
                },
              },
            ]}
          />
        ) : (
          <DynamicTable
            headers={[
              "SN",
              "VOUCHER",
              "CATEGORY",
              "PAYMENT AMOUNT",
              "PREV REMAINING",
              "REMAINING",
              "STATUS",
              "METHOD",
              "PAYMENT DATE",
              "RECORDED BY",
              "DESCRIPTION",
              "ATTACHMENT",
            ]}
            data={historyTableData}
            loading={loading}
            height="420px"
            page={historyPage}
            rowsPerPage={historyRowsPerPage}
            totalRows={filteredHistories.length}
            totalPaidAmount={historyPaymentTotal}
            handleChangePage={(_e, newPage) => setHistoryPage(newPage)}
            handleChangeRowsPerPage={(e) => {
              setHistoryRowsPerPage(parseInt(e.target.value, 10));
              setHistoryPage(0);
            }}
          />
        )}
      </Box>

      {/* Create / Update */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: editing ? 560 : 500,
            width: "100%",
          },
        }}
      >
        <DialogTitle>
          {editing ? "Update Expense Payment" : "Add Expense Payment"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={categories}
                getOptionLabel={(option) => option.categoryName || ""}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={
                  categories.find((c) => c._id === form.expenseCategoryId) || null
                }
                onChange={(_e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    expenseCategoryId: newValue?._id || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Expense Category"
                    placeholder="Search category"
                  />
                )}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Total Amount"
                type="number"
                value={form.totalAmount}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, totalAmount: e.target.value }))
                }
                helperText="How much is the total expense?"
              />
            </Grid>
            {!editing && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Initial Paid Amount"
                  type="number"
                  value={form.paidAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({ ...prev, paidAmount: value }));
                    if (!(Number(value) > 0)) setFormFile(null);
                  }}
                  helperText="How much is being paid now?"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                required={isPaymentMethodRequired}
                label="Payment Method"
                value={form.paymentMethod}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    paymentMethod: e.target.value,
                  }))
                }
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                helperText={
                  isPaymentMethodRequired
                    ? "How is this payment being made?"
                    : "Optional when initial paid amount is 0"
                }
              >
                <MenuItem value="">
                  <em>Select payment method</em>
                </MenuItem>
                {PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Remaining Amount"
                value={formatCurrency(autoRemaining)}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                helperText="Automatically calculated"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment Status"
                value={autoStatus}
                disabled
                InputLabelProps={{ shrink: true }}
                helperText="Automatically calculated from the amounts"
              >
                {PAYMENT_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Payment Date"
                InputLabelProps={{ shrink: true }}
                value={form.paymentDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, paymentDate: e.target.value }))
                }
                helperText="Date of this payment"
              />
            </Grid>
            {!editing && Number(form.paidAmount || 0) > 0 && (
              <Grid item xs={12}>
                {renderAttachmentPicker(
                  formFile,
                  setFormFile,
                  "Optional when initial paid amount is greater than 0"
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
                helperText="Optional notes"
              />
            </Grid>
          </Grid>

          {editing && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Payment History
              </Typography>
              {renderHistoryList(formHistory)}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={submitForm}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving..." : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Payment */}
      <Dialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5, fontSize: 13 }}>
            Current Remaining:{" "}
            <b>{formatCurrency(paymentTarget?.remainingAmount || 0)}</b>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Payment Amount"
                type="number"
                value={paymentForm.paymentAmount}
                onChange={(e) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    paymentAmount: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  label="Payment Method"
                  value={paymentForm.paymentMethod}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                >
                  {PAYMENT_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status (After)</InputLabel>
                <Select label="Payment Status (After)" value={paymentPreviewStatus} disabled>
                  {PAYMENT_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Remaining After Payment"
                value={formatCurrency(paymentPreviewRemaining)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Payment Date"
                InputLabelProps={{ shrink: true }}
                value={paymentForm.paymentDate}
                onChange={(e) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    paymentDate: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderAttachmentPicker(paymentFile, setPaymentFile)}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={paymentForm.description}
                onChange={(e) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Payment History</Typography>
          {renderHistoryList(paymentHistory)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={paying}
            onClick={submitPayment}
            sx={{ textTransform: "none" }}
          >
            {paying ? "Saving..." : "Record Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment History dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Payment History
          {historyTarget?.voucher ? ` — Voucher #${historyTarget.voucher}` : ""}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label={historyTarget?.paymentStatus || "--"}
              color={statusColor(historyTarget?.paymentStatus || "") as any}
              sx={{ mr: 1 }}
            />
            <Typography component="span" sx={{ fontSize: 13 }}>
              Remaining:{" "}
              <b style={{ color: "red" }}>
                {formatCurrency(historyTarget?.remainingAmount || 0)}
              </b>
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {renderHistoryList(historyRows)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Expense Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this expense payment
            {deleteTarget?.voucher ? ` (Voucher #${deleteTarget.voucher})` : ""}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMarkPaidDialog}
        onClose={() => !markingPaid && setOpenMarkPaidDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm Mark as Paid</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The following unpaid / partially paid expenses will be marked as
            Paid (remaining amount will be cleared):
          </Typography>
          <TableContainer sx={{ maxHeight: 280, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Voucher</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedExpensesForPay.map((expense, index) => (
                  <TableRow key={expense._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{expense.voucher ?? "--"}</TableCell>
                    <TableCell>{expense.description || "--"}</TableCell>
                    <TableCell>
                      {expense.category?.categoryName ||
                        expense.expenseCategory ||
                        "--"}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        expense.remainingAmount || expense.totalAmount || 0
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#e8f5e9",
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              Total to be Paid ({selectedExpensesForPay.length} item
              {selectedExpensesForPay.length === 1 ? "" : "s"})
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "#2e7d32" }}>
              {formatCurrency(selectedPayTotal)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenMarkPaidDialog(false)}
            variant="outlined"
            size="small"
            disabled={markingPaid}
            sx={{
              borderColor: "#055FFC",
              color: "#055FFC",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkMarkAsPaid}
            variant="contained"
            size="small"
            disabled={markingPaid || selectedExpensesForPay.length === 0}
            sx={{
              backgroundColor: "#2e7d32",
              textTransform: "none",
              "&:hover": { backgroundColor: "#1b5e20" },
            }}
          >
            {markingPaid ? "Updating..." : "Confirm Mark Paid"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          {selectedImageUrl && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <img
                src={selectedImageUrl}
                alt="Attachment Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const nextSibling = e.currentTarget.nextSibling as HTMLElement;
                  if (nextSibling) nextSibling.style.display = "block";
                }}
              />
              <Typography variant="body2" sx={{ display: "none" }}>
                Unable to load image
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImageModal}
            variant="outlined"
            size="small"
            sx={{ borderColor: "#055FFC", color: "#055FFC", textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpensePaymentsPage;
